import { createTheme, PaletteMode } from "@mui/material/styles";

// Modern color palette with sophisticated gradients
const lightPalette = {
  mode: "light" as PaletteMode,
  primary: {
    main: "#0f172a",
    light: "#1e293b",
    dark: "#020617",
  },
  secondary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
  },
  background: {
    default: "#f8fafc",
    paper: "#ffffff",
  },
  text: {
    primary: "#0f172a",
    secondary: "#475569",
  },
  success: {
    main: "#22c55e",
  },
  error: {
    main: "#ef4444",
  },
};

const darkPalette = {
  mode: "dark" as PaletteMode,
  primary: {
    main: "#f8fafc",
    light: "#f1f5f9",
    dark: "#e2e8f0",
  },
  secondary: {
    main: "#818cf8",
    light: "#a5b4fc",
    dark: "#6366f1",
  },
  background: {
    default: "#0f172a",
    paper: "#1e293b",
  },
  text: {
    primary: "#f8fafc",
    secondary: "#cbd5e1",
  },
  success: {
    main: "#22c55e",
  },
  error: {
    main: "#ef4444",
  },
};

const theme = (mode: "light" | "dark") =>
  createTheme({
    palette: mode === "light" ? lightPalette : darkPalette,
    typography: {
      fontFamily: "'Inter', 'Geist', system-ui, sans-serif",
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        letterSpacing: "-0.025em",
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 600,
        fontSize: "2rem",
        letterSpacing: "-0.025em",
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.5rem",
        letterSpacing: "-0.025em",
        lineHeight: 1.4,
      },
      body1: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: 1.6,
        color: mode === "light" ? "#475569" : "#cbd5e1",
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 12,
            fontWeight: 500,
            padding: "12px 24px",
            transition: "all 0.2s ease-in-out",
            background: mode === "light" 
              ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
              : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            color: mode === "light" ? "#fff" : "#0f172a",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: mode === "light"
                ? "0 4px 12px rgba(15, 23, 42, 0.15)"
                : "0 4px 12px rgba(248, 250, 252, 0.15)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          },
          containedSecondary: {
            backgroundColor: mode === "light" ? "#6366f1" : "#818cf8",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: mode === "light" ? "#4f46e5" : "#a5b4fc",
            },
            "&:disabled": {
              backgroundColor: mode === "light" ? "#e0e7ff" : "#475569",
              color: "#cbd5e1",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            boxShadow: mode === "light"
              ? "0 4px 20px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)"
              : "0 4px 20px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.3)",
            backgroundColor: mode === "light" ? "#ffffff" : "#1e293b",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: mode === "light"
                ? "0 8px 30px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1)"
                : "0 8px 30px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.3)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              transition: "all 0.2s ease-in-out",
              backgroundColor: mode === "light" ? "#ffffff" : "#1e293b",
              "& fieldset": {
                borderColor: mode === "light" ? "#e2e8f0" : "#334155",
                transition: "all 0.2s ease-in-out",
              },
              "&:hover fieldset": {
                borderColor: mode === "light" ? "#cbd5e1" : "#475569",
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === "light" ? "#6366f1" : "#818cf8",
                borderWidth: "2px",
              },
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingTop: "32px",
            paddingBottom: "32px",
            backgroundColor: mode === "light" ? "#ffffff" : "#0f172a",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  });

export default theme;